import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  User,
  UserCredential,
} from 'firebase/auth';
import { auth } from '@/config/firebase';

export interface AuthError {
  code: string;
  message: string;
}

export interface SignUpData {
  email: string;
  password: string;
  name: string;
}

export interface SignInData {
  email: string;
  password: string;
}

/**
 * Create a new user account
 */
export async function signUp({ email, password, name }: SignUpData): Promise<UserCredential> {
  try {
    // Log environment info for debugging production issues
    if (import.meta.env.MODE === 'production') {
      console.log('Production signup attempt:', {
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        apiUrl: import.meta.env.VITE_API_BASE_URL,
        email: email.substring(0, 3) + '***' // Partial email for privacy
      });
    }

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Update the user's display name
    await updateProfile(userCredential.user, {
      displayName: name,
    });

    return userCredential;
  } catch (error: any) {
    // Enhanced error handling for signup failures
    console.error('Firebase signup error:', {
      code: error.code,
      message: error.message,
      stack: error.stack,
      environment: import.meta.env.MODE,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    });

    throw {
      code: error.code,
      message: getAuthErrorMessage(error.code, email),
    } as AuthError;
  }
}

/**
 * Sign in an existing user
 */
export async function signIn({ email, password }: SignInData): Promise<UserCredential> {
  try {
    return await signInWithEmailAndPassword(auth, email, password);
  } catch (error: any) {
    // Enhanced error handling for invalid credentials
    console.error('Firebase signin error:', {
      code: error.code,
      message: error.message,
      email: email.substring(0, 3) + '***', // Partial email for privacy
      environment: import.meta.env.MODE,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    });

    throw {
      code: error.code,
      message: getAuthErrorMessage(error.code, email),
    } as AuthError;
  }
}

/**
 * Sign in with Google
 */
export async function signInWithGoogle(): Promise<UserCredential> {
  try {
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');

    const userCredential = await signInWithPopup(auth, provider);

    // Log environment info for debugging production issues
    if (import.meta.env.MODE === 'production') {
      console.log('Production Google signin successful:', {
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        email: userCredential.user.email?.substring(0, 3) + '***' // Partial email for privacy
      });
    }

    return userCredential;
  } catch (error: any) {
    // Enhanced error handling for Google signin failures
    console.error('Firebase Google signin error:', {
      code: error.code,
      message: error.message,
      environment: import.meta.env.MODE,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    });

    throw {
      code: error.code,
      message: getAuthErrorMessage(error.code),
    } as AuthError;
  }
}

/**
 * Sign out the current user
 */
export async function signOutUser(): Promise<void> {
  try {
    await signOut(auth);
  } catch (error: any) {
    throw {
      code: error.code,
      message: getAuthErrorMessage(error.code),
    } as AuthError;
  }
}

/**
 * Send password reset email
 */
export async function resetPassword(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error: any) {
    throw {
      code: error.code,
      message: getAuthErrorMessage(error.code),
    } as AuthError;
  }
}

/**
 * Get current user
 */
export function getCurrentUser(): User | null {
  return auth.currentUser;
}

/**
 * Convert Firebase auth error codes to user-friendly messages
 */
function getAuthErrorMessage(errorCode: string, email?: string): string {
  switch (errorCode) {
    case 'auth/user-not-found':
      return 'No account found with this email address. Please check your email or sign up for a new account.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again or use "Forgot Password" to reset it.';
    case 'auth/invalid-credential':
      return email
        ? `Invalid login credentials for ${email}. This email may be registered with Google sign-in only. Try signing in with Google instead, or check your password.`
        : 'Invalid login credentials. Please check your email and password, or try signing in with Google.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Try signing in instead.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters long.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later or reset your password.';
    case 'auth/network-request-failed':
      return getNetworkErrorMessage();
    case 'auth/app-not-authorized':
      return 'This app is not authorized to use Firebase Authentication. Please check your Firebase configuration.';
    case 'auth/api-key-not-valid':
      return 'Firebase API key configuration error. This is a server-side issue. Please contact support.';
    case 'auth/invalid-api-key':
      return 'Firebase API key configuration error. This is a server-side issue. Please contact support.';
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email using a different sign-in method. Try signing in with Google.';
    case 'auth/credential-already-in-use':
      return 'This credential is already associated with a different user account.';
    default:
      return 'An unexpected error occurred. Please try again or contact support if the problem persists.';
  }
}

/**
 * Get detailed network error message with troubleshooting steps
 */
function getNetworkErrorMessage(): string {
  const isDev = import.meta.env.DEV;
  const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;

  if (isDev && authDomain?.includes('demo-project')) {
    return 'Network error: You are using demo Firebase configuration. Please either:\n' +
           '1. Set up Firebase emulators (run "firebase emulators:start")\n' +
           '2. Update your .env file with real Firebase project credentials';
  }

  if (isDev) {
    return 'Network error: Please check if Firebase emulators are running or verify your internet connection.';
  }

  return 'Network error. Please check your internet connection and try again.';
}
